import React from 'react';
import { DollarSign, CreditCard, ShoppingBag, FileText, Package, FilePlus } from 'lucide-react';

/**
 * Компонент секции оборотных активов для формы расчета ликвидности
 */
const CurrentAssetsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  const isDetailedView = formData.settings.includeDetailedAssets;

  // Расчет итоговой суммы оборотных активов
  const totalCurrentAssets = Object.values(formData.currentAssets)
    .reduce((sum, value) => sum + (parseFloat(String(value).replace(/\s+/g, '').replace(',', '.')) || 0), 0);

  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <DollarSign className="w-5 h-5 mr-2 text-blue-500" />
        Оборотные активы
      </h3>

      {isDetailedView ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Денежные средства */}
          <div>
            <label htmlFor="currentAssets.cash" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <CreditCard className="w-4 h-4 mr-1.5 text-blue-500" />
              Денежные средства
            </label>
            <input
              type="text"
              id="currentAssets.cash"
              name="currentAssets.cash"
              value={formData.currentAssets.cash}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentAssets.cash'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentAssets.cash'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentAssets.cash']}
              </p>
            )}
          </div>

          {/* Краткосрочные финансовые вложения */}
          <div>
            <label htmlFor="currentAssets.shortTermInvestments" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <CreditCard className="w-4 h-4 mr-1.5 text-blue-500" />
              Краткосрочные фин. вложения
            </label>
            <input
              type="text"
              id="currentAssets.shortTermInvestments"
              name="currentAssets.shortTermInvestments"
              value={formData.currentAssets.shortTermInvestments}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentAssets.shortTermInvestments'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentAssets.shortTermInvestments'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentAssets.shortTermInvestments']}
              </p>
            )}
          </div>

          {/* Дебиторская задолженность */}
          <div>
            <label htmlFor="currentAssets.accountsReceivable" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <FileText className="w-4 h-4 mr-1.5 text-blue-500" />
              Дебиторская задолженность
            </label>
            <input
              type="text"
              id="currentAssets.accountsReceivable"
              name="currentAssets.accountsReceivable"
              value={formData.currentAssets.accountsReceivable}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentAssets.accountsReceivable'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentAssets.accountsReceivable'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentAssets.accountsReceivable']}
              </p>
            )}
          </div>

          {/* Запасы */}
          <div>
            <label htmlFor="currentAssets.inventory" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Package className="w-4 h-4 mr-1.5 text-blue-500" />
              Запасы
            </label>
            <input
              type="text"
              id="currentAssets.inventory"
              name="currentAssets.inventory"
              value={formData.currentAssets.inventory}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentAssets.inventory'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentAssets.inventory'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentAssets.inventory']}
              </p>
            )}
          </div>

          {/* НДС по приобретенным ценностям */}
          <div>
            <label htmlFor="currentAssets.vatReceivable" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <FilePlus className="w-4 h-4 mr-1.5 text-blue-500" />
              НДС по приобретенным ценностям
            </label>
            <input
              type="text"
              id="currentAssets.vatReceivable"
              name="currentAssets.vatReceivable"
              value={formData.currentAssets.vatReceivable}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentAssets.vatReceivable'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentAssets.vatReceivable'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentAssets.vatReceivable']}
              </p>
            )}
          </div>

          {/* Прочие оборотные активы */}
          <div>
            <label htmlFor="currentAssets.otherCurrentAssets" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <ShoppingBag className="w-4 h-4 mr-1.5 text-blue-500" />
              Прочие оборотные активы
            </label>
            <input
              type="text"
              id="currentAssets.otherCurrentAssets"
              name="currentAssets.otherCurrentAssets"
              value={formData.currentAssets.otherCurrentAssets}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentAssets.otherCurrentAssets'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentAssets.otherCurrentAssets'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentAssets.otherCurrentAssets']}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {/* Единственное поле при отключенной детализации */}
          <div>
            <label htmlFor="currentAssets.total" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <DollarSign className="w-4 h-4 mr-1.5 text-blue-500" />
              Всего оборотных активов
            </label>
            <input
              type="text"
              id="totalCurrentAssets"
              value={totalCurrentAssets.toLocaleString('ru-RU')}
              className="form-input w-full bg-gray-100 dark:bg-gray-600"
              readOnly
            />
          </div>
        </div>
      )}

      {/* Итоговая сумма (в детализированном режиме) */}
      {isDetailedView && (
        <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Всего оборотных активов:
            </span>
            <span className="text-lg font-semibold text-blue-700 dark:text-blue-300">
              {totalCurrentAssets.toLocaleString('ru-RU')} ₽
            </span>
          </div>
        </div>
      )}

      {/* Информационная подсказка */}
      <div className="mt-4 bg-blue-100 dark:bg-blue-900/40 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              Что включать в оборотные активы
            </h4>
            <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
              В оборотные активы согласно РСБУ (ПБУ) входят: денежные средства, краткосрочные финансовые вложения, дебиторская задолженность, запасы, НДС по приобретенным ценностям и прочие оборотные активы.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentAssetsSection;